/**
 * Personalised ordering for the "You may also like" row.
 *
 * `sections/you-may-also-like.liquid` shows a merchandised collection, so every
 * visitor sees the same products in the same order. This reorders that row
 * against the individual visitor's browsing history, so what they are most
 * likely to care about leads.
 *
 * How it fits together:
 *   - `add-to-recently-viewed.js` records viewed handles (unchanged by this file).
 *   - `product-affinity.js` records the *attributes* of viewed products
 *     (type / tags / vendor) under a separate storage key.
 *   - The section renders a wider pool than it displays (see `pool_multiplier`),
 *     giving this something to actually choose between, and tags each slide with
 *     `data-affinity-*` attributes to score against.
 *   - This element wraps the slider and reorders the rendered slide nodes in
 *     place. It moves existing DOM nodes rather than re-parsing markup, so
 *     Swiper's configuration and element references survive intact.
 *
 * Ordering is a boost, not a filter: every product the section rendered stays
 * eligible and nothing is forced in or out. A visitor with no history gets the
 * collection's original order, which is the pre-existing behaviour. The one
 * exception is the product currently being viewed, which is always excluded.
 *
 * All signals are first-party and stay in the visitor's own browser. Nothing is
 * transmitted, and no personal data is stored — only product attributes.
 */

const AFFINITY_KEY_PREFIX = 'product_affinity_';
const VIEWED_KEY_PREFIX = 'recently_viewed_handles_';

/** Scoring weights. Type is the strongest signal, vendor the weakest. */
const WEIGHT_TYPE = 3;
const WEIGHT_TAG = 2;
const WEIGHT_VENDOR = 1;

/**
 * A product the visitor already opened is demoted, not boosted. They have seen it
 * and moved on, so the row earns its place by surfacing things they have *not*
 * seen that match their demonstrated taste. The product is still eligible — it is
 * pushed down, never filtered out.
 */
const PENALTY_ALREADY_VIEWED = 0.4;

/**
 * Recency decay. The most recent view counts full, each older one slightly less,
 * so a visitor's current session outweighs what they browsed a fortnight ago.
 */
const RECENCY_DECAY = 0.85;

/** Products viewed longer ago than this contribute nothing. */
const MAX_HISTORY = 24;

/** Read and validate a JSON array from localStorage. Never throws. */
function readStoredList(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // Malformed entry, or storage blocked (Safari private browsing).
    // No history simply means no personalisation.
    return [];
  }
}

/**
 * Build a weighted profile of what this visitor has been looking at.
 *
 * Returns maps of attribute -> accumulated weight, where a product viewed more
 * recently contributes more. Attribute values are lowercased so casing
 * differences between products do not split a signal.
 */
function buildProfile(shopId) {
  const history = readStoredList(AFFINITY_KEY_PREFIX + shopId).slice(0, MAX_HISTORY);

  const types = new Map();
  const tags = new Map();
  const vendors = new Map();

  const add = (map, value, weight) => {
    if (!value) return;
    const key = String(value).trim().toLowerCase();
    if (!key) return;
    map.set(key, (map.get(key) || 0) + weight);
  };

  history.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;

    // Most recent entry is index 0 and decays from there.
    const recency = Math.pow(RECENCY_DECAY, index);

    add(types, entry.type, recency);
    add(vendors, entry.vendor, recency);
    if (Array.isArray(entry.tags)) {
      entry.tags.forEach((tag) => add(tags, tag, recency));
    }
  });

  return { types, tags, vendors };
}

/** Look up an attribute's accumulated weight, case-insensitively. */
function profileWeight(map, value) {
  if (!value) return 0;
  return map.get(String(value).trim().toLowerCase()) || 0;
}

/**
 * Score one candidate slide against the visitor profile.
 * Higher is more relevant; 0 means no overlap with anything they have viewed.
 */
function scoreSlide(slide, profile, viewedHandles) {
  const handle = slide.dataset.affinityHandle;
  const type = slide.dataset.affinityType;
  const vendor = slide.dataset.affinityVendor;
  const tags = (slide.dataset.affinityTags || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

  let score = 0;

  score += profileWeight(profile.types, type) * WEIGHT_TYPE;
  score += profileWeight(profile.vendors, vendor) * WEIGHT_VENDOR;

  // Tags are capped so a product carrying many tags cannot dominate purely by
  // surface area — relevance should come from strong overlap, not tag count.
  const tagScore = tags.reduce((sum, tag) => sum + profileWeight(profile.tags, tag), 0);
  score += Math.min(tagScore, 3) * WEIGHT_TAG;

  // Demote anything already viewed, most strongly for the most recent views —
  // the product they just came from should not lead its own recommendation row.
  if (handle && viewedHandles.includes(handle)) {
    const position = viewedHandles.indexOf(handle);
    const recency = Math.pow(RECENCY_DECAY, position);
    score *= 1 - PENALTY_ALREADY_VIEWED * recency;
  }

  return score;
}

/**
 * Re-rank slides by affinity, trim to `limit`, and return them.
 *
 * Sorting is stable on score ties, so Shopify's own ordering is preserved
 * wherever this has no opinion — including for a visitor with no history.
 */
function rankSlides(slides, profile, viewedHandles, limit) {
  const scored = slides.map((slide, index) => ({
    slide,
    index,
    score: scoreSlide(slide, profile, viewedHandles),
  }));

  scored.sort((a, b) => (b.score - a.score) || (a.index - b.index));

  const ranked = limit > 0 ? scored.slice(0, limit) : scored;
  return ranked.map((entry) => entry.slide);
}

if (!customElements.get('personalised-recommendations')) {
  customElements.define(
    'personalised-recommendations',
    class PersonalisedRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.dataset.disabled === 'true') return;

        // Slides are rendered server-side by Liquid, so they are usually present
        // the moment this upgrades. Reorder immediately when they are; otherwise
        // wait, which covers a section re-rendered by the theme editor.
        if (this.slides().length > 0) {
          this.personalise();
          return;
        }

        const observer = new MutationObserver(() => {
          if (this.slides().length === 0) return;
          observer.disconnect();
          this.personalise();
        });
        observer.observe(this, { childList: true, subtree: true });

        // Never observe indefinitely — a section that renders no products would
        // otherwise leave an observer attached for the life of the page.
        setTimeout(() => observer.disconnect(), 10000);
      }

      container() {
        return this.querySelector('[data-swiper-container]');
      }

      slides() {
        const container = this.container();
        return container ? Array.from(container.querySelectorAll(':scope > [data-swiper-slide]')) : [];
      }

      personalise() {
        try {
          const container = this.container();
          const slides = this.slides();
          if (!container || slides.length === 0) return;

          const shopId = this.dataset.shopId;
          const limit = parseInt(this.dataset.displayLimit, 10) || 0;

          const profile = buildProfile(shopId);
          const viewedHandles = readStoredList(VIEWED_KEY_PREFIX + shopId).slice(0, MAX_HISTORY);

          // Never recommend the product the visitor is currently looking at.
          const currentHandle = this.currentProductHandle();

          const candidates = currentHandle
            ? slides.filter((slide) => slide.dataset.affinityHandle !== currentHandle)
            : slides;

          const hasHistory =
            viewedHandles.length > 0 ||
            profile.types.size > 0 ||
            profile.tags.size > 0 ||
            profile.vendors.size > 0;

          // Trim the widened pool back to the display count even with no history,
          // so rendering extra candidates never changes what an unpersonalised
          // visitor sees.
          const ordered = hasHistory
            ? rankSlides(candidates, profile, viewedHandles, limit)
            : candidates.slice(0, limit > 0 ? limit : candidates.length);

          if (ordered.length === 0) return; // Nothing to show; leave the row alone.

          // Drop the surplus, then reattach the winners in order. appendChild
          // MOVES an existing node rather than copying it, so nothing is
          // re-parsed and no listeners are lost.
          const keep = new Set(ordered);
          slides.forEach((slide) => {
            if (!keep.has(slide)) slide.remove();
          });
          ordered.forEach((slide) => container.appendChild(slide));

          this.reapplyPositionalStyling(ordered);

          // Let Swiper re-measure now the slide count and order have changed.
          const slideshow = this.querySelector('slideshow-section');
          if (slideshow && slideshow.swiper) {
            slideshow.swiper.update();
            slideshow.swiper.slideTo(0, 0);
          }
        } catch (e) {
          // Personalisation is an enhancement. On any failure the visitor keeps
          // the row exactly as the theme rendered it.
          console.error('[personalised recommendations]', e);
        }
      }

      /**
       * `--animation-order` and the featured-card class are both positional and
       * were written by Liquid against the original order, so they have to be
       * re-applied once the slides have been sorted.
       */
      reapplyPositionalStyling(ordered) {
        const featureFirst = this.dataset.featureFirst === 'true';

        ordered.forEach((slide, index) => {
          const card = slide.querySelector('.wt-bestsellers__card');
          if (!card) return;

          if (card.style.getPropertyValue('--animation-order')) {
            card.style.setProperty('--animation-order', index + 1);
          }

          if (featureFirst) {
            card.classList.toggle('wt-bestsellers__card--featured', index === 0);
          }
        });
      }

      /**
       * The handle of the product whose page this is, so it can be excluded.
       * Read from the recently-viewed recorder, which Liquid renders with the
       * current product's handle on every product page.
       */
      currentProductHandle() {
        const recorder = document.querySelector('product-affinity[data-handle], add-to-recently-viewed[data-handle]');
        return recorder ? recorder.dataset.handle : '';
      }
    }
  );
}
